import noop from 'lodash/noop';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Session from 'supertokens-auth-react/recipe/session';

import { useApiClient } from '@eluve/api-client-provider';
import { formatTimeRemaining } from '@eluve/date-utils';

import { useProvidedSessionContext } from './SessionContextProvider';
import { useSession } from './useSession';

/**
 * Hook for managing user impersonation functionality.
 * Only available to eluve-admin users.
 */
export const useImpersonation = () => {
  const apiClient = useApiClient();
  const sessionContext = useProvidedSessionContext();
  const [isLoading, setIsLoading] = useState(false);

  const session = useSession();
  const isImpersonating = session.impersonation !== undefined;

  const expiresAt = useMemo(
    () =>
      session.impersonation?.expiresAt
        ? new Date(session.impersonation.expiresAt * 1000)
        : undefined,
    [session.impersonation?.expiresAt],
  );

  const renewImpersonation = useCallback(async () => {
    if (!isImpersonating) {
      throw new Error('Not currently impersonating a user');
    }

    setIsLoading(true);
    try {
      const result = await apiClient.auth.renewImpersonation({ body: {} });

      // 400 = impersonation already over server-side, client stale. Reconcile
      // via refresh, then leave for the admin's own page. Never reload in
      // place — that re-opens the modal. Swallow refresh errors so a transient
      // failure can't fall back into the reload loop.
      if (result.status === 400) {
        await Session.attemptRefreshingSession().catch(noop);
        window.location.href = '/';
        return;
      }

      if (result.status !== 200) {
        throw new Error('Failed to renew impersonation');
      }

      if (sessionContext.refetch) {
        await sessionContext.refetch();
      } else {
        window.location.reload();
      }
    } catch {
      window.location.reload();
    } finally {
      setIsLoading(false);
    }
  }, [isImpersonating, apiClient, sessionContext]);

  const exitImpersonation = useCallback(
    async (redirectUrl?: string) => {
      if (!isImpersonating) {
        throw new Error('Not currently impersonating a user');
      }

      setIsLoading(true);
      try {
        const result = await apiClient.auth.exitImpersonation({ body: {} });

        // 400 = impersonation already over server-side, client stale.
        // Reconcile via refresh, then leave for the admin's own page. Never
        // reload in place — that re-opens the modal. Swallow refresh errors so
        // a transient failure can't fall back into the reload loop.
        if (result.status === 400) {
          await Session.attemptRefreshingSession().catch(noop);
          window.location.href = redirectUrl ?? '/';
          return;
        }

        if (result.status !== 200) {
          throw new Error('Failed to exit impersonation');
        }

        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          window.location.reload();
        }
      } catch {
        window.location.reload();
      } finally {
        setIsLoading(false);
      }
    },
    [isImpersonating, apiClient],
  );

  return {
    isEluveAdmin: session.isEluveAdmin,
    isImpersonating,
    isLoading,
    expiresAt,
    impersonationOriginUrl: session.impersonation?.originUrl,
    exitImpersonation,
    renewImpersonation,
  };
};

/**
 * Hook to get auto-updating time remaining for impersonation
 * @param expiresAt - The expiration date (from impersonationExpiresAt)
 * @param format - 'short' for "5m" format, 'long' for "5m remaining" format
 * @param updateIntervalMs - How often to update (default: 1000ms for short, 60000ms for long)
 * @returns Formatted time string or null
 */
export const useImpersonationTimeRemaining = (
  expiresAt: Date | undefined,
  format: 'short' | 'long' = 'short',
  updateIntervalMs?: number,
): string | null => {
  const [timeRemaining, setTimeRemaining] = useState<string | null>(() =>
    formatTimeRemaining(expiresAt, format),
  );

  // Default update intervals: 1s for short format (shows seconds), 1min for long format
  const interval = updateIntervalMs ?? (format === 'short' ? 1000 : 60000);

  useEffect(() => {
    if (!expiresAt) {
      setTimeRemaining(null);
      return;
    }

    const updateTime = () => {
      setTimeRemaining(formatTimeRemaining(expiresAt, format));
    };

    // Update immediately
    updateTime();

    // Then update on interval
    const intervalId = setInterval(updateTime, interval);

    return () => clearInterval(intervalId);
  }, [expiresAt, format, interval]);

  return timeRemaining;
};
